import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, updateDoc, arrayRemove, arrayUnion, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../Universal/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { AlignJustify, ArrowLeft, ArrowRight, ChevronDown, CircleHelp, Eye, SquareCheck, SquareX, Hourglass, Lock, LockOpen } from 'lucide-react';
import { GlassContainer } from '../../../styles';
import { irtScoreBundle } from '../../../utils/irtMst';
import {
  getAmcqIrtCutsFromAssignment,
  getAmcqQuestionsForScoring,
  isPanelGraphAssignment,
  isTerminalModule,
  pickNextModuleId,
  routeBandFromTheta,
} from '../../../utils/amcqPanelGraph';
import { sampleAmcqModuleQuestions } from '../../../utils/amcqModuleSample';

const questionKey = (q) => (q && (q.questionId || q.question)) || '';

const difficultyBandFromScore = (score) => {
  const n = parseFloat(score);
  if (Number.isNaN(n)) return 'Medium';
  if (n <= 0.9) return 'Easy';
  if (n <= 1.9) return 'Medium';
  return 'Hard';
};

const getModuleOrder = (questions) => {
  if (!questions || !questions.length) return [1];
  const nums = new Set(questions.map((q) => q.sectorNumber || 1));
  return Array.from(nums).sort((a, b) => a - b);
};

const choiceLetters = (q) => {
  const n = Array.isArray(q.choices) ? q.choices.length : 0;
  if (n === 0) return ['a', 'b', 'c', 'd'];
  return Array.from({ length: n }, (_, i) => String.fromCharCode(97 + i));
};

const choiceText = (q, letter) => {
  const ch = String(letter || 'a').toLowerCase();
  const i = ch.charCodeAt(0) - 97;
  return (q.choices && q.choices[i]) || '';
};

const explanationAt = (q, letter) => {
  const ch = String(letter || 'a').toLowerCase();
  const i = ch.charCodeAt(0) - 97;
  return (q.explanations && q.explanations[i]) || '';
};

const getQuestionsForModule = (questions, moduleNum) => {
  if (!questions || !questions.length) return [];
  return questions
    .filter((q) => (q.sectorNumber || 1) === moduleNum)
    .sort((a, b) => {
      const ds = (parseFloat(a.difficultyScore) || 0) - (parseFloat(b.difficultyScore) || 0);
      if (ds !== 0) return ds;
      return String(a.question || '').localeCompare(String(b.question || ''));
    });
};

/** Full module list, optionally capped to a deterministic random subset when IRT is off. */
const resolveAmcqModuleQuestionList = ({
  assignment,
  assignmentId,
  currentModule,
  currentModuleId,
  panelModuleQuestionsById,
}) => {
  if (!assignment) return [];
  let full = [];
  let moduleKey = '';
  if (isPanelGraphAssignment(assignment) && currentModuleId) {
    full = panelModuleQuestionsById[currentModuleId] || [];
    moduleKey = `id:${currentModuleId}`;
  } else {
    full = getQuestionsForModule(assignment.questions || [], currentModule);
    moduleKey = `mod:${currentModule}`;
  }
  if (assignment.irtScoring === true) return full;
  const cap = Math.floor(Number(assignment.amcqRandomQuestionsPerModule));
  const moduleSampleCap = Number.isFinite(cap) && cap >= 1 ? cap : 0;
  const uid = auth.currentUser?.uid || '';
  if (moduleSampleCap < 1 || !uid || full.length === 0) return full;
  const seed = `${assignmentId}|${uid}|${moduleKey}|v1`;
  return sampleAmcqModuleQuestions({
    fullList: full,
    cap: moduleSampleCap,
    seedKey: seed,
  });
};

const computePercent = (correct, incorrect) => {
  const c = Array.isArray(correct) ? correct : [];
  const ic = Array.isArray(incorrect) ? incorrect : [];
  const t = c.length + ic.length;
  if (t === 0) return 0;
  return Math.round((c.length / t) * 100);
};

const assignmentForIrt = (a) =>
  a ? { ...a, questions: getAmcqQuestionsForScoring(a) } : a;

const findQuestionInAssignment = (assignment, questionStem) => {
  const pool = getAmcqQuestionsForScoring(assignment || {});
  return (pool || []).find((q) => q && q.question === questionStem) || null;
};

/** Percent-correct or IRT 0–100 scale score from graded lists. */
const scoreFromGradedLists = (assignment, correct, incorrect) => {
  if (assignment?.irtScoring) {
    return irtScoreBundle(assignmentForIrt(assignment), correct, incorrect).squareScore;
  }
  return computePercent(correct, incorrect);
};

/** Whether this question counts as done for the nav map (checked, or legacy key match). */
const isQuestionMarkedCompleted = (q, completedList) => {
  if (!q || !Array.isArray(completedList)) return false;
  const k = questionKey(q);
  if (k && completedList.includes(k)) return true;
  if (q.questionId != null && q.questionId !== '' && completedList.includes(q.questionId)) return true;
  if (q.question != null && q.question !== '' && completedList.includes(q.question)) return true;
  return false;
};

const getChoiceStyle = (choice) => {
  const ch = String(choice || 'a').toLowerCase();
  const styles = {
    a: { background: '#B6C2FF', color: '#020CFF', variant: 'blue' },
    b: { background: '#B4F9BC', color: '#2BB514', variant: 'green' },
    c: { background: '#FFECAF', color: '#F4A700', variant: 'yellow' },
    d: { background: '#F6C0FF', color: '#E01FFF', variant: 'pink' },
    e: { background: '#ADFFFB', color: '#00AAB7', variant: 'teal' },
  };
  return styles[ch] || { background: '#E0E0E0', color: '#000000', variant: 'clear' };
};

const TakeAmcq = () => {
  const { assignmentId } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [typedAnswer, setTypedAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [SquareScore, setSquareScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeLimit, setTimeLimit] = useState(null);
  const [showTimer, setShowTimer] = useState(true);
  const [assignmentName, setAssignmentName] = useState('');
  const [saveAndExit, setSaveAndExit] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [retypedAnswer, setRetypedAnswer] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [correctQuestions, setCorrectQuestions] = useState([]);
  const [incorrectQuestions, setIncorrectQuestions] = useState([]);
  const [currentModule, setCurrentModule] = useState(1);
  const [moduleQuestionIndex, setModuleQuestionIndex] = useState(0);
  const [lockedSectorNumbers, setLockedSectorNumbers] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showModuleInstantFeedback, setShowModuleInstantFeedback] = useState(false);
  const [moduleInstantFeedbackRows, setModuleInstantFeedbackRows] = useState([]);
  const [moduleResponses, setModuleResponses] = useState({});
  const [showModuleReview, setShowModuleReview] = useState(false);
  const [classId, setClassId] = useState(null);
  const navigate = useNavigate();
  const choiceRefs = useRef({});
  const endTestRef = useRef(async () => {});
  const [questionOrder, setQuestionOrder] = useState(0);
  const [mstRoutingHistory, setMstRoutingHistory] = useState([]);
  const [currentModuleId, setCurrentModuleId] = useState(null);
  const [panelModuleQuestionsById, setPanelModuleQuestionsById] = useState({});
  const [panelModuleLoading, setPanelModuleLoading] = useState(false);
  const [lockdown, setLockdown] = useState(false);
  const [showMyResponse, setShowMyResponse] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLockTooltip, setShowLockTooltip] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const holdTimerRef = useRef(null);
  const moduleResponsesRef = useRef({});

  useEffect(() => {
    moduleResponsesRef.current = moduleResponses;
  }, [moduleResponses]);

  // Add all useEffect hooks at the top
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    Object.keys(choiceRefs.current).forEach((choice) => {
      const el = choiceRefs.current[choice];
      if (el && el.parentElement) {
        const height = Math.max(el.scrollHeight, 22);
        el.parentElement.style.minHeight = `${height + 2}px`;
      }
    });
  }, [currentQuestion, selectedAnswer, typedAnswer, showModuleReview]);

  useEffect(() => {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('contextmenu', (e) => e.preventDefault());
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const loadStudent = async () => {
      const userRef = doc(db, 'students', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const d = userDoc.data();
        setFirstName(d.firstName || '');
        setLastName(d.lastName || '');
        if (d.takeAmcqTutorialSeen !== true) setShowTutorial(true);
      }
    };
    loadStudent();
  }, []);

  useEffect(() => {
    const fetchAssignment = async () => {
      const assignmentDoc = await getDoc(doc(db, 'assignments', assignmentId));
      if (assignmentDoc.exists()) {
        const data = assignmentDoc.data();
        setSaveAndExit(data.saveAndExit);
        setClassId(data.classId);
        setAssignment(data);
        setTimeLimit(data.timer * 60);
        setLockdown(data.lockdown || false);
        setAssignmentName(data.assignmentName);
        setShowTimer(data.timer > 0);

        const resumed = await fetchSavedProgress(data);
        if (!resumed) {
          if (isPanelGraphAssignment(data)) {
            setCurrentModuleId(data.amcqEntryModuleId);
            setCurrentModule(1);
            setModuleQuestionIndex(0);
            setLockedSectorNumbers([]);
            setQuestionOrder(0);
            setMstRoutingHistory([]);
            setPanelModuleQuestionsById({});
          } else if (data.questions && data.questions.length > 0) {
            const order = getModuleOrder(data.questions);
            const firstMod = order[0] || 1;
            setCurrentModule(firstMod);
            setCurrentModuleId(null);
            setModuleQuestionIndex(0);
            setLockedSectorNumbers([]);
            setQuestionOrder(0);
            setMstRoutingHistory([]);
            setPanelModuleQuestionsById({});
          }
        }
      }
    };
    fetchAssignment();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per assignmentId; helpers close over latest handlers
  }, [assignmentId]);

  useEffect(() => {
    if (timeLimit !== null && timeLimit > 0) {
      setSecondsLeft(timeLimit);
      setTimerStarted(true);
      setShowTimer(true);
    } else {
      setShowTimer(false);
      setTimerStarted(false);
    }
  }, [timeLimit]);

  useEffect(() => {
    let timerId;
  
    if (secondsLeft > 0 && timerStarted) {
      timerId = setInterval(() => {
        setSecondsLeft((prevSeconds) => prevSeconds - 1);
      }, 1000);
    } else if (secondsLeft === 0 && timerStarted) {
      endTestRef.current?.();
    }
  
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [secondsLeft, timerStarted]);

  // Add cleanup effect for hold timer
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
      }
    };
  }, []);

  const handleHoldStart = () => {
    setIsHolding(true);
    let startTime = Date.now();
    
    holdTimerRef.current = setInterval(() => {
      const progress = Math.min(((Date.now() - startTime) / 1500) * 100, 100);
      setHoldProgress(progress);
      
      if (progress >= 100) {
        clearInterval(holdTimerRef.current);
        setShowConfirmModal(false);
        endTestRef.current?.();
      }
    }, 10);
  };

  const handleHoldEnd = () => {
    setIsHolding(false);
    setHoldProgress(0);
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
    }
  };

  const handleSubmitClick = () => {
    setShowConfirmModal(true);
  };

  const handleAnswerSelect = (choice) => {
    setSelectedAnswer(choice);
    const needRetype = assignment?.retypeEnabled !== false;
    const nextTyped = !needRetype && currentQuestion ? choiceText(currentQuestion, choice) : '';
    if (!needRetype && currentQuestion) {
      setTypedAnswer(nextTyped);
    } else {
      setTypedAnswer('');
    }
    if (currentQuestion) {
      const key = questionKey(currentQuestion);
      if (!completedQuestions.includes(key)) {
        setModuleResponses((prev) => ({
          ...prev,
          [key]: { choice, typed: nextTyped },
        }));
      }
    }
    if (needRetype) {
      setTimeout(() => {
        const textarea = document.getElementById(`textarea-${choice}`);
        if (textarea) {
          textarea.focus();
        }
      }, 0);
    }
  };

  const fetchSavedProgress = async (assignmentData) => {
    if (!assignmentData) return false;

    const progressRef = doc(db, 'assignments(progress)', `${assignmentId}_${auth.currentUser.uid}`);
    const progressDoc = await getDoc(progressRef);
    if (!progressDoc.exists()) return false;

    const data = progressDoc.data();
    const correctObjs = data.correctQuestions || [];
    const incorrectObjs = data.incorrectQuestions || [];
    const comp = data.completedQuestions || [];

    setSquareScore(
      typeof data.SquareScore === 'number' && !Number.isNaN(data.SquareScore)
        ? data.SquareScore
        : scoreFromGradedLists(assignmentData, correctObjs, incorrectObjs)
    );
    setCorrectQuestions(correctObjs);
    setIncorrectQuestions(incorrectObjs);
    setCompletedQuestions(comp);
    if (data.timeRemaining != null) setSecondsLeft(data.timeRemaining);
    setTimerStarted(true);

    if (isPanelGraphAssignment(assignmentData)) {
      const mid =
        typeof data.currentModuleId === 'string' && data.currentModuleId
          ? data.currentModuleId
          : assignmentData.amcqEntryModuleId;
      setCurrentModuleId(mid);
      if (typeof data.currentModule === 'number') {
        setCurrentModule(data.currentModule);
      }
      const rawIdx = typeof data.moduleQuestionIndex === 'number' ? data.moduleQuestionIndex : 0;
      setModuleQuestionIndex(Math.max(0, rawIdx));
      const locked = Array.isArray(data.lockedSectorNumbers) ? data.lockedSectorNumbers : [];
      setLockedSectorNumbers(locked);
      if (data.moduleResponses && typeof data.moduleResponses === 'object') {
        setModuleResponses(data.moduleResponses);
      }
      setQuestionOrder(typeof data.questionOrder === 'number' && !Number.isNaN(data.questionOrder) ? data.questionOrder : 0);
      setMstRoutingHistory(Array.isArray(data.mstRoutingHistory) ? data.mstRoutingHistory : []);
      return true;
    }

    const order = getModuleOrder(assignmentData.questions);
    const mod =
      typeof data.currentModule === 'number' && order.includes(data.currentModule)
        ? data.currentModule
        : order[0] || 1;
    setCurrentModule(mod);
    setCurrentModuleId(null);

    const list = resolveAmcqModuleQuestionList({
      assignment: assignmentData,
      assignmentId,
      currentModule: mod,
      currentModuleId: null,
      panelModuleQuestionsById: {},
    });
    if (list.length === 0) {
      await endTestRef.current?.(
        typeof data.SquareScore === 'number' && !Number.isNaN(data.SquareScore)
          ? data.SquareScore
          : scoreFromGradedLists(assignmentData, correctObjs, incorrectObjs)
      );
      return true;
    }

    const rawIdx = typeof data.moduleQuestionIndex === 'number' ? data.moduleQuestionIndex : 0;
    const idx = Math.min(Math.max(0, rawIdx), list.length - 1);
    setModuleQuestionIndex(idx);

    const locked = Array.isArray(data.lockedSectorNumbers) ? data.lockedSectorNumbers : [];
    setLockedSectorNumbers(locked);

    if (data.moduleResponses && typeof data.moduleResponses === 'object') {
      setModuleResponses(data.moduleResponses);
    }

    setQuestionOrder(typeof data.questionOrder === 'number' && !Number.isNaN(data.questionOrder) ? data.questionOrder : 0);
    setMstRoutingHistory(Array.isArray(data.mstRoutingHistory) ? data.mstRoutingHistory : []);

    return true;
  };

  // Timer
  const toggleTimer = () => {
    setShowTimer((prevShowTimer) => !prevShowTimer);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const closeFeedbackOnly = () => {
    setShowConfetti(false);
    setShowFeedback(false);
    setShowExplanation(false);
    setShowMyResponse(false);
    setRetypedAnswer('');
  };

  const formatQuestionData = (question, selectedChoice = null, meta = {}) => {
    if (!question) {
      return {
        question: '',
        selectedChoice,
        choices: [],
        explanations: [],
        correctChoice: '',
        ...(typeof meta.order === 'number' && !Number.isNaN(meta.order) ? { order: meta.order } : {}),
      };
    }
    return {
      questionId: question.questionId || question.question,
      question: question.question,
      ...(question.sectorNumber != null && question.sectorNumber !== ''
        ? { sectorNumber: question.sectorNumber }
        : {}),
      ...(question.difficultyScore != null &&
      question.difficultyScore !== '' &&
      !Number.isNaN(parseFloat(question.difficultyScore))
        ? {
            difficultyScore: question.difficultyScore,
            difficultyBand: difficultyBandFromScore(question.difficultyScore),
          }
        : {}),
      selectedChoice,
      correctChoice: question.correctChoice || '',
      choices: question.choices || [],
      explanations: question.explanations || [],
      ...(typeof meta.order === 'number' && !Number.isNaN(meta.order) ? { order: meta.order } : {}),
    };
  };

  const handleSaveAndExit = async () => {
    const progressRef = doc(db, 'assignments(progress)', `${assignmentId}_${auth.currentUser.uid}`);
    const irtBundle =
      assignment?.irtScoring === true
        ? irtScoreBundle(assignmentForIrt(assignment), correctQuestions, incorrectQuestions)
        : null;

    try {
      await setDoc(progressRef, {
        studentUid: auth.currentUser.uid,
        assignmentId: assignmentId,
        classId: assignment.classId || null,
        format: 'AMCQ',
        progressSchemaVersion: 1,
        timeRemaining: secondsLeft,
        status: 'in progress',
        SquareScore: SquareScore,
        questionOrder,
        currentModule,
        moduleQuestionIndex,
        lockedSectorNumbers,
        moduleResponses,
        completedQuestions: completedQuestions,
        mstLayoutSnapshot: assignment.mstLayout && typeof assignment.mstLayout === 'object' ? assignment.mstLayout : null,
        mstRoutingHistory,
        currentModuleId: currentModuleId || null,
        correctQuestions: correctQuestions.map((questionData) => {
          const originalQuestion = findQuestionInAssignment(assignment, questionData.question);
          return formatQuestionData(originalQuestion, questionData.correctChoice, {
            order: questionData.order,
          });
        }),
        incorrectQuestions: incorrectQuestions.map((questionData) => {
          const originalQuestion = findQuestionInAssignment(assignment, questionData.question);
          return formatQuestionData(originalQuestion, questionData.chosenAnswer, {
            order: questionData.order,
          });
        }),
        savedAt: new Date(),
        ...(irtBundle && {
          irtTheta: irtBundle.theta,
          irtSe: irtBundle.se,
          mstRouteHint: irtBundle.routeHint,
        }),
      });

      const studentRef = doc(db, 'students', auth.currentUser.uid);
      await updateDoc(studentRef, {
        assignmentsToTake: arrayRemove(assignmentId),
        assignmentsInProgress: arrayUnion(assignmentId),
      });

      navigate(`/studentassignments/${classId}/active`);
    } catch (error) {
      console.error('Error saving progress:', error);
      alert(
        `There was an error saving your progress. Please try again.${
          error?.message ? `\n\n${error.message}` : ''
        }`
      );
    }
  };

  const endTest = useCallback(
    async (finalScoreOrSnapshot = null) => {
      if (!assignment) return;

      let compList = completedQuestions;
      let corrList = correctQuestions;
      let incorrList = incorrectQuestions;

      if (
        finalScoreOrSnapshot != null &&
        typeof finalScoreOrSnapshot === 'object' &&
        !Array.isArray(finalScoreOrSnapshot)
      ) {
        const snap = finalScoreOrSnapshot;
        corrList = snap.correctQuestions ?? correctQuestions;
        incorrList = snap.incorrectQuestions ?? incorrectQuestions;
        compList = snap.completedQuestions ?? completedQuestions;
      }

      const irtOn = assignment.irtScoring === true;
      let pct;
      let irtExtras = {};

      if (irtOn) {
        const bundle = irtScoreBundle(assignmentForIrt(assignment), corrList, incorrList);
        pct = bundle.squareScore;
        irtExtras = {
          scoringMode: 'irt_eap_2pl',
          irtTheta: bundle.theta,
          irtSe: bundle.se,
          mstRouteHint: bundle.routeHint,
        };
      } else if (
        finalScoreOrSnapshot != null &&
        typeof finalScoreOrSnapshot === 'object' &&
        !Array.isArray(finalScoreOrSnapshot) &&
        typeof finalScoreOrSnapshot.scorePct === 'number' &&
        !Number.isNaN(finalScoreOrSnapshot.scorePct)
      ) {
        pct = finalScoreOrSnapshot.scorePct;
      } else if (
        finalScoreOrSnapshot != null &&
        typeof finalScoreOrSnapshot === 'number' &&
        !Number.isNaN(finalScoreOrSnapshot)
      ) {
        pct = finalScoreOrSnapshot;
      } else {
        pct = computePercent(corrList, incorrList);
      }

      const studentRef = doc(db, 'students', auth.currentUser.uid);
      const gradeRef = doc(db, 'grades', `${assignmentId}_${auth.currentUser.uid}`);

      try {
        await setDoc(gradeRef, {
          studentUid: auth.currentUser.uid,
          firstName,
          lastName,
          classId: assignment.classId,
          assignmentId: assignmentId,
          assignmentName: assignmentName,
          format: 'AMCQ',
          SquareScore: pct,
          maxScore: 100,
          completedQuestions: compList,
          correctQuestions: corrList.map((questionData) => {
            const originalQuestion = findQuestionInAssignment(assignment, questionData.question);
            return formatQuestionData(originalQuestion, questionData.correctChoice, {
              order: questionData.order,
            });
          }),
          incorrectQuestions: incorrList.map((questionData) => {
            const originalQuestion = findQuestionInAssignment(assignment, questionData.question);
            return formatQuestionData(originalQuestion, questionData.chosenAnswer, {
              order: questionData.order,
            });
          }),
          submittedAt: new Date(),
          viewable: false,
          questionOrder,
          ...(assignment?.mstLayout && typeof assignment.mstLayout === 'object' ? { mstLayout: assignment.mstLayout } : {}),
          ...(Array.isArray(mstRoutingHistory) && mstRoutingHistory.length > 0 ? { mstRoutingHistory } : {}),
          ...irtExtras,
        });

        await updateDoc(studentRef, {
          assignmentsToTake: arrayRemove(assignmentId),
          assignmentsInProgress: arrayRemove(assignmentId),
          assignmentsTaken: arrayUnion(assignmentId),
          [`class_${assignment.classId}.grades.${assignmentId}`]: {
            score: pct,
            submittedAt: new Date(),
            assignmentId,
            assignmentName,
            format: 'AMCQ',
          },
        });

        const progressRef = doc(db, 'assignments(progress)', `${assignmentId}_${auth.currentUser.uid}`);
        await deleteDoc(progressRef);

        navigate(`/studentassignments/${classId}/active`);
      } catch (error) {
        console.error('Error submitting assignment:', error);
        alert(
          `There was an error submitting your assignment. Please try again.${
            error?.message ? `\n\n${error.message}` : ''
          }`
        );
      }
    },
    [
      assignment,
      assignmentId,
      assignmentName,
      classId,
      completedQuestions,
      correctQuestions,
      firstName,
      incorrectQuestions,
      lastName,
      mstRoutingHistory,
      navigate,
      questionOrder,
      currentModuleId,
    ]
  );

  endTestRef.current = endTest;

  const moduleQuestionsList = useMemo(
    () =>
      resolveAmcqModuleQuestionList({
        assignment,
        assignmentId,
        currentModule,
        currentModuleId,
        panelModuleQuestionsById,
      }),
    [assignment, assignmentId, currentModule, currentModuleId, panelModuleQuestionsById]
  );

  const allModuleQuestionsChecked = useMemo(
    () =>
      moduleQuestionsList.length > 0 &&
      moduleQuestionsList.every((q) => completedQuestions.includes(questionKey(q))),
    [moduleQuestionsList, completedQuestions]
  );

  useEffect(() => {
    if (!assignment) return;
    const list = moduleQuestionsList;
    if (!list.length) {
      setCurrentQuestion(null);
      return;
    }
    const clampedIdx = Math.min(Math.max(0, moduleQuestionIndex), list.length - 1);
    if (clampedIdx !== moduleQuestionIndex) {
      setModuleQuestionIndex(clampedIdx);
    }
    setCurrentQuestion(list[clampedIdx]);
  }, [assignment, moduleQuestionsList, moduleQuestionIndex]);

  useEffect(() => {
    if (!assignment || !isPanelGraphAssignment(assignment) || !currentModuleId) return;
    let cancelled = false;
    setPanelModuleLoading(true);
    const ref = doc(db, 'assignments', assignmentId, 'amcq_modules', currentModuleId);
    getDoc(ref)
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setPanelModuleQuestionsById((prev) => ({ ...prev, [currentModuleId]: [] }));
          setPanelModuleLoading(false);
          return;
        }
        const d = snap.data();
        const qs = Array.isArray(d.questions) ? d.questions : [];
        setPanelModuleQuestionsById((prev) => ({ ...prev, [currentModuleId]: qs }));
        if (typeof d.stage === 'number' && Number.isFinite(d.stage)) {
          setCurrentModule(d.stage);
        }
        setPanelModuleLoading(false);
      })
      .catch(() => {
        if (!cancelled) setPanelModuleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assignment, assignmentId, currentModuleId]);

  useEffect(() => {
    if (!currentQuestion) return;
    const q = currentQuestion;
    const key = questionKey(q);
    const corr = correctQuestions.find((c) => c && c.question === q.question);
    const inc = incorrectQuestions.find((i) => i && i.question === q.question);
    if (corr) {
      const lett = String(corr.correctChoice || 'a').toLowerCase();
      setSelectedAnswer(lett);
      setTypedAnswer(choiceText(q, lett));
    } else if (inc) {
      const lett = String(inc.chosenAnswer || 'a').toLowerCase();
      setSelectedAnswer(lett);
      setTypedAnswer(choiceText(q, lett));
    } else {
      const saved = moduleResponsesRef.current[key];
      setSelectedAnswer(saved?.choice ? String(saved.choice).toLowerCase() : '');
      setTypedAnswer(saved?.typed || '');
    }
  }, [currentQuestion, correctQuestions, incorrectQuestions]);

  const confirmTutorialDone = async () => {
    setShowTutorial(false);
    try {
      await updateDoc(doc(db, 'students', auth.currentUser.uid), { takeAmcqTutorialSeen: true });
    } catch (e) {
      console.error(e);
    }
  };

  const handlePrevQuestionInModule = () => {
    closeFeedbackOnly();
    if (showModuleReview) {
      setShowModuleReview(false);
      setModuleQuestionIndex(Math.max(0, moduleQuestionsList.length - 1));
      return;
    }
    if (moduleQuestionsList.length <= 1) return;
    setModuleQuestionIndex((i) => (i <= 0 ? moduleQuestionsList.length - 1 : i - 1));
  };

  const handleNextQuestionInModule = () => {
    closeFeedbackOnly();
    if (showModuleReview) return;
    if (moduleQuestionsList.length <= 1) {
      setShowModuleReview(true);
      return;
    }
    if (moduleQuestionIndex >= moduleQuestionsList.length - 1) {
      setShowModuleReview(true);
    } else {
      setModuleQuestionIndex((i) => i + 1);
    }
  };

  const advanceModuleOrFinish = () => {
    if (!assignment) return;
    closeFeedbackOnly();
    setShowModuleInstantFeedback(false);
    setModuleInstantFeedbackRows([]);
    setShowModuleReview(false);

    if (isPanelGraphAssignment(assignment) && currentModuleId) {
      const cuts = getAmcqIrtCutsFromAssignment(assignment);
      const bundle = irtScoreBundle(
        assignmentForIrt(assignment),
        correctQuestions,
        incorrectQuestions
      );
      const band = routeBandFromTheta(bundle.theta, cuts);
      const edges = assignment.amcqRoutingGraph[currentModuleId];
      const nextId = pickNextModuleId(edges, band);
      if (!nextId) {
        endTestRef.current?.();
        return;
      }
      setLockedSectorNumbers((prev) => [...new Set([...prev, currentModule])]);
      setCurrentModuleId(nextId);
      setModuleQuestionIndex(0);
      return;
    }

    const order = getModuleOrder(assignment.questions);
    const idx = order.indexOf(currentModule);
    if (idx < 0) return;
    if (idx >= order.length - 1) {
      endTestRef.current?.();
      return;
    }
    setLockedSectorNumbers((prev) => [...new Set([...prev, currentModule])]);
    setCurrentModule(order[idx + 1]);
    setModuleQuestionIndex(0);
  };

  const handleNextModule = () => {
    if (!assignment) return;
    const allChecked =
      moduleQuestionsList.length > 0 &&
      moduleQuestionsList.every((q) => completedQuestions.includes(questionKey(q)));
    if (!allChecked) {
      alert('Use the review step (last bubble in Questions) and check your answers before continuing.');
      return;
    }
    closeFeedbackOnly();
    advanceModuleOrFinish();
  };

  const handleCheckEntireModule = () => {
    if (!assignment || !moduleQuestionsList.length) return;
    const unchecked = moduleQuestionsList.filter((q) => !completedQuestions.includes(questionKey(q)));
    if (unchecked.length === 0) {
      alert('This module is already checked.');
      return;
    }
    const retypeOn = assignment.retypeEnabled !== false;
    let nextCompleted = [...completedQuestions];
    let nextCorrect = [...correctQuestions];
    let nextIncorrect = [...incorrectQuestions];
    let nextOrder = questionOrder;
    const instantFeedbackRows = [];

    for (let i = 0; i < moduleQuestionsList.length; i++) {
      const q = moduleQuestionsList[i];
      const key = questionKey(q);
      if (nextCompleted.includes(key)) continue;

      const r = moduleResponsesRef.current[key];
      const sel = (r?.choice || '').toLowerCase();
      if (!sel) {
        alert(`Select an answer for question ${i + 1}.`);
        return;
      }
      const selectedText = choiceText(q, sel);
      if (retypeOn && selectedText.toLowerCase() !== (r?.typed || '').toLowerCase()) {
        alert(`For question ${i + 1}, type the full answer text to match your choice.`);
        return;
      }

      const correctLetter = (q.correctChoice || '').toLowerCase();
      const isCorrect = sel === correctLetter;
      nextCompleted = [...nextCompleted, key];
      nextOrder += 1;

      instantFeedbackRows.push({
        questionText: q.question,
        chosenLetter: sel,
        chosenText: choiceText(q, sel),
        correctLetter,
        correctText: choiceText(q, correctLetter),
        explanationChosen: explanationAt(q, sel),
        explanationCorrect: explanationAt(q, correctLetter),
        isCorrect,
      });

      if (isCorrect) {
        nextCorrect = [
          ...nextCorrect,
          {
            question: q.question,
            correctChoice: sel,
            correctExplanation: explanationAt(q, sel),
            choiceContent: choiceText(q, sel),
            order: nextOrder,
          },
        ];
      } else {
        nextIncorrect = [
          ...nextIncorrect,
          {
            question: q.question,
            chosenAnswer: sel,
            correctAnswer: correctLetter,
            correctExplanation: explanationAt(q, correctLetter),
            incorrectExplanation: explanationAt(q, sel),
            choiceContent: choiceText(q, sel),
            correctContent: choiceText(q, correctLetter),
            order: nextOrder,
          },
        ];
      }
    }

    const clearDrafts = (prev) => {
      const next = { ...prev };
      moduleQuestionsList.forEach((q) => {
        delete next[questionKey(q)];
      });
      return next;
    };

    setShowModuleReview(false);

    const nextScore = scoreFromGradedLists(assignment, nextCorrect, nextIncorrect);

    if (
      isPanelGraphAssignment(assignment) ||
      (assignment?.mstLayout && typeof assignment.mstLayout.totalQuestions === 'number')
    ) {
      const b = irtScoreBundle(assignmentForIrt(assignment), nextCorrect, nextIncorrect);
      setMstRoutingHistory((prev) => [
        ...prev,
        {
          moduleNumber: currentModule,
          ...(typeof currentModuleId === 'string' && currentModuleId
            ? { moduleId: currentModuleId }
            : {}),
          theta: b.theta,
          se: b.se,
          routeHint: b.routeHint,
          squareScoreAtCheckpoint: b.squareScore,
          at: new Date().toISOString(),
        },
      ]);
    }

    if (assignment.feedback === 'instant') {
      setSquareScore(nextScore);
      setCorrectQuestions(nextCorrect);
      setIncorrectQuestions(nextIncorrect);
      setCompletedQuestions(nextCompleted);
      setQuestionOrder(nextOrder);
      setModuleResponses(clearDrafts);
      setModuleInstantFeedbackRows(instantFeedbackRows);
      setShowModuleInstantFeedback(true);
      return;
    }

    flushSync(() => {
      setSquareScore(nextScore);
      setCorrectQuestions(nextCorrect);
      setIncorrectQuestions(nextIncorrect);
      setCompletedQuestions(nextCompleted);
      setQuestionOrder(nextOrder);
      setModuleResponses(clearDrafts);
    });

    if (isPanelGraphAssignment(assignment) && currentModuleId) {
      const cuts = getAmcqIrtCutsFromAssignment(assignment);
      const bundle = irtScoreBundle(assignmentForIrt(assignment), nextCorrect, nextIncorrect);
      const band = routeBandFromTheta(bundle.theta, cuts);
      const edges = assignment.amcqRoutingGraph[currentModuleId];
      const nextId = pickNextModuleId(edges, band);
      if (!nextId) {
        endTestRef.current?.({
          scorePct: nextScore,
          completedQuestions: nextCompleted,
          correctQuestions: nextCorrect,
          incorrectQuestions: nextIncorrect,
        });
      } else {
        setLockedSectorNumbers((prev) => [...new Set([...prev, currentModule])]);
        setCurrentModuleId(nextId);
        setModuleQuestionIndex(0);
      }
      return;
    }

    const order = getModuleOrder(assignment.questions);
    const idx = order.indexOf(currentModule);
    if (idx >= order.length - 1) {
      endTestRef.current?.({
        scorePct: nextScore,
        completedQuestions: nextCompleted,
        correctQuestions: nextCorrect,
        incorrectQuestions: nextIncorrect,
      });
    } else {
      setLockedSectorNumbers((prev) => [...new Set([...prev, currentModule])]);
      setCurrentModule(order[idx + 1]);
      setModuleQuestionIndex(0);
    }
  };

  // Retype
  const handleRetypeAnswer = (e) => {
    if (!currentQuestion) return;
    const input = e.target.value;
    const correctLetter = (currentQuestion.correctChoice || 'a').toLowerCase();
    const correctAnswer = choiceText(currentQuestion, correctLetter);

    let convertedInput = input
      .split('')
      .map((char, index) => {
        if (index < correctAnswer.length) {
          return correctAnswer[index] === correctAnswer[index].toUpperCase()
            ? char.toUpperCase()
            : char.toLowerCase();
        }
        return char;
      })
      .join('');

    if (correctAnswer.toLowerCase().startsWith(convertedInput.toLowerCase())) {
      setRetypedAnswer(convertedInput);
    }
  };

  const isRetypedAnswerCorrect = () => {
    if (!currentQuestion) return false;
    const correctLetter = (currentQuestion.correctChoice || '').toLowerCase();
    const correctAnswer = choiceText(currentQuestion, correctLetter).toLowerCase();
    const userAnswer = retypedAnswer.toLowerCase();
    return correctAnswer === userAnswer || (Math.abs(correctAnswer.length - userAnswer.length) <= 1 && 
           (correctAnswer.includes(userAnswer) || userAnswer.includes(correctAnswer)));
  };

  const handleTyping = (e) => {
    if (!currentQuestion || !selectedAnswer) return;
    const selectedText = choiceText(currentQuestion, selectedAnswer);
    const input = e.target.value;

    let convertedInput = input
      .split('')
      .map((char, index) => {
        if (index < selectedText.length) {
          return selectedText[index] === selectedText[index].toUpperCase()
            ? char.toUpperCase()
            : char.toLowerCase();
        }
        return char;
      })
      .join('');

    if (selectedText.toLowerCase().startsWith(convertedInput.toLowerCase())) {
      setTypedAnswer(convertedInput);
      const key = questionKey(currentQuestion);
      if (!completedQuestions.includes(key)) {
        setModuleResponses((prev) => ({
          ...prev,
          [key]: { choice: selectedAnswer, typed: convertedInput },
        }));
      }
    }
  };
  const isTypedAnswerCorrect = () => {
    if (!currentQuestion || !selectedAnswer) return false;
    if (assignment?.retypeEnabled === false) return true;
    if (!typedAnswer) return false;
    const selectedText = choiceText(currentQuestion, selectedAnswer);
    return selectedText.toLowerCase() === typedAnswer.toLowerCase();
  };

  const moduleOrder = useMemo(() => {
    if (!assignment) return [1];
    if (isPanelGraphAssignment(assignment)) {
      const n = Math.max(1, Math.floor(Number(assignment.amcqMaxStage)) || 1);
      return Array.from({ length: n }, (_, i) => i + 1);
    }
    return getModuleOrder(assignment.questions || []);
  }, [assignment]);
  const modulePosition =
    moduleOrder.indexOf(currentModule) >= 0 ? moduleOrder.indexOf(currentModule) + 1 : 1;
  const totalModules = moduleOrder.length || 1;
  const isLastModule = useMemo(() => {
    if (!assignment) return true;
    if (isPanelGraphAssignment(assignment) && currentModuleId) {
      return isTerminalModule(assignment.amcqRoutingGraph, currentModuleId);
    }
    const mo = getModuleOrder(assignment.questions || []);
    return mo.indexOf(currentModule) === mo.length - 1;
  }, [assignment, currentModule, currentModuleId]);

  const nextModuleDisplayNum = useMemo(() => {
    if (!assignment || isLastModule) return null;
    if (isPanelGraphAssignment(assignment)) {
      const nextPos = modulePosition + 1;
      return nextPos <= totalModules ? nextPos : null;
    }
    const mo = getModuleOrder(assignment.questions || []);
    const idx = mo.indexOf(currentModule);
    if (idx < 0 || idx >= mo.length - 1) return null;
    return mo[idx + 1];
  }, [assignment, isLastModule, currentModule, modulePosition, totalModules]);

  const moduleSubmitContinueLabel = isLastModule
    ? 'Submit & finish'
    : nextModuleDisplayNum != null
      ? `Submit & continue to module ${nextModuleDisplayNum}`
      : 'Submit & continue';

  if (!assignment) {
    return <div style={{ padding: '40px', fontFamily: "'montserrat', sans-serif" }}>Loading...</div>;
  }
  const hasAssignableContent =
    (assignment.questions && assignment.questions.length > 0) ||
    (isPanelGraphAssignment(assignment) && assignment.amcqEntryModuleId);
  if (!hasAssignableContent) {
    return (
      <div style={{ padding: '40px', fontFamily: "'montserrat', sans-serif" }}>
        This assignment has no questions.
      </div>
    );
  }
  if (isPanelGraphAssignment(assignment) && panelModuleLoading) {
    return <div style={{ padding: '40px', fontFamily: "'montserrat', sans-serif" }}>Loading...</div>;
  }
  if (moduleQuestionsList.length === 0) {
    return (
      <div style={{ padding: '40px', fontFamily: "'montserrat', sans-serif" }}>
        No questions in this module.
      </div>
    );
  }
  if (!currentQuestion && !showModuleReview) {
    return <div style={{ padding: '40px', fontFamily: "'montserrat', sans-serif" }}>Loading...</div>;
  }

  const currentQuestionKey = currentQuestion ? questionKey(currentQuestion) : '';
  const alreadyAnsweredThis = currentQuestionKey ? completedQuestions.includes(currentQuestionKey) : false;

  const renderChoice = (choice) => {
    if (!currentQuestion) return null;
    const style = getChoiceStyle(choice);
    const content = choiceText(currentQuestion, choice);
    const retypeOn = assignment.retypeEnabled !== false;
    const selectedHere = selectedAnswer === choice;
    const typingComplete = !retypeOn || alreadyAnsweredThis || isTypedAnswerCorrect();
    const showChoiceColor = selectedHere && typingComplete;

    const commonContentStyle = {
      fontWeight: '500',
      fontSize: '16px',
      textAlign: 'left',
      margin: 0,
      color: showChoiceColor ? style.color : 'grey',
      userSelect: 'none',
      pointerEvents: 'none',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      width: '100%',
      lineHeight: 1.45,
    };

    if (selectedAnswer === choice) {
      const glassVariant = showChoiceColor ? style.variant : 'grey';
      const textTint = showChoiceColor ? style.color : '#888888';

      return (
        <div style={{ margin: '4px 0', width: '100%' }}>
          <GlassContainer
            key={choice}
            onClick={() => handleAnswerSelect(choice)}
            variant={glassVariant}
            size={0}
            style={{
              width: '100%',
            }}
            contentStyle={{
              width: '100%',
              padding: '4px 12px',
              display: 'flex',
              alignItems: 'flex-start',
              position: 'relative',
              zIndex: '2',
            }}
          >
            <div style={{ 
              width: '100%',
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-start',
              padding: '0 8px 0 28px',
              boxSizing: 'border-box',
            }}>
              <p ref={el => choiceRefs.current[choice] = el} style={{ ...commonContentStyle, color: textTint }}>
                {content}
              </p>
              {retypeOn && !alreadyAnsweredThis ? (
                <textarea
                  id={`textarea-${choice}`}
                  value={typedAnswer}
                  onChange={handleTyping}
                  style={{
                    fontFamily: "'montserrat', sans-serif",
                    fontWeight: '500',
                    position: 'absolute',
                    top: '0',
                    left: '28px',
                    right: '8px',
                    bottom: '0',
                    minHeight: '100%',
                    fontSize: '16px',
                    textAlign: 'left',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: textTint,
                    resize: 'none',
                    overflow: 'hidden',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    boxSizing: 'border-box',
                    lineHeight: 1.45,
                    padding: '0',
                  }}
                />
              ) : null}
            </div>
          </GlassContainer>
        </div>
      );
    }

    return (
      <div style={{ margin: '4px 0', width: '100%' }}>
        <GlassContainer
          key={choice}
          variant="clear"
          size={0}
          onClick={() => handleAnswerSelect(choice)}
          style={{
            width: '100%',
            cursor: 'pointer',
          }}
          contentStyle={{
            width: '100%',
            padding: '4px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            position: 'relative',
          }}
        >
          <div style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '0 4px 0 0',
            boxSizing: 'border-box',
          }}>
            <p ref={el => choiceRefs.current[choice] = el} style={commonContentStyle}>
              {content}
            </p>
          </div>
        </GlassContainer>
      </div>
    );
};

  // Handle hold start
  // Handle hold end
  // Clean up timer on unmount
  // Separate submit button click handler from actual submit
  // Update the existing submit button click handler to use the new confirmation flow
  const onSaveAndExit = async () => {
    await handleSaveAndExit();
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      fontFamily: "'montserrat', sans-serif"
    }}>
      {showTutorial && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <GlassContainer
            variant="clear"
            size={2}
            style={{ maxWidth: '520px', width: '100%' }}
            contentStyle={{
              padding: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: '#222', fontFamily: "'Montserrat', sans-serif" }}>
              How this assignment works
            </h2>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#555', fontSize: '0.95rem', lineHeight: 1.6, fontFamily: "'Montserrat', sans-serif" }}>
              <li>The <strong>header</strong> shows <strong>Modules</strong> and <strong>Questions</strong> as glass bubbles. Tap a question bubble to jump to that item.</li>
              <li>Use the <strong>arrows</strong> on the sides to move between questions. From the last question, the right arrow opens review, or tap the <strong>summary icon</strong> bubble at the end of the Questions row.</li>
              <li>With <strong>Retype</strong> on, your selected choice uses a <strong>grey</strong> glass style until your typing matches the answer text, then it shows the letter color. There is no Check button on each question.</li>
              <li>On the review screen you see each prompt and your answer in its choice color. Tap a block to jump back. Tap <strong>Check all answers</strong> to grade the module. With <strong>Instant</strong> feedback you then see correct answers and explanations.</li>
              <li>With <strong>At completion</strong> feedback, after you check a module you move on immediately with no results screen. Use <strong>Next module</strong> or <strong>Finish</strong> in the header when it appears. Earlier modules stay locked.</li>
              <li>Use <strong>Save &amp; Exit</strong> or <strong>Submit</strong> in the header when you need to.</li>
            </ul>
            <div style={{ marginLeft: 'auto', marginTop: '8px' }}>
              <GlassContainer
                variant="green"
                size={0}
                onClick={confirmTutorialDone}
                style={{ cursor: 'pointer', minWidth: '120px' }}
                contentStyle={{
                  padding: '10px 22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ color: '#2BB514', fontSize: '0.9rem', fontWeight: '500', fontFamily: "'Montserrat', sans-serif" }}>Got it</span>
              </GlassContainer>
            </div>
          </GlassContainer>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <GlassContainer
            variant="clear"
            size={2}
            style={{
              width: '400px',
              backgroundColor: 'white'
            }}
            contentStyle={{
              padding: '30px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}
          >
            <h2 style={{
              margin: 0,
              fontSize: '1.2rem',
              fontWeight: '500',
              color: 'black',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              Submit Assignment
            </h2>
            <p style={{
              margin: 0,
              fontSize: '1rem',
              color: 'grey',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              Are you sure you want to submit? You won't be able to make any changes after submission.
            </p>
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '50px',
                  padding: '5px 15px',
                  color: 'grey',
                  cursor: 'pointer',
                  fontSize: '.9rem',
                  fontWeight: '400',
                  width: '100px',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Cancel
              </button>
              <div style={{ position: 'relative', width: '150px' }}>
                <GlassContainer
                  onMouseDown={handleHoldStart}
                  onMouseUp={handleHoldEnd}
                  onMouseLeave={handleHoldEnd}
                  onTouchStart={handleHoldStart}
                  onTouchEnd={handleHoldEnd}
                  variant="green"
                  size={0}
                  style={{
                    width: '100%',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  contentStyle={{
                    padding: '5px 15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  <span style={{
                    color: '#2BB514',
                    fontSize: '.9rem',
                    fontWeight: '400',
                    fontFamily: "'Montserrat', sans-serif"
                  }}>
                    Submit (Hold)
                  </span>
                  {isHolding && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${holdProgress}%`,
                      backgroundColor: 'rgba(43, 181, 20, 0.1)',
                      transition: 'width 0.01s linear',
                      zIndex: 1
                    }} />
                  )}
                </GlassContainer>
              </div>
            </div>
          </GlassContainer>
        </div>
      )}

      {showModuleInstantFeedback && moduleInstantFeedbackRows.length > 0 && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1900,
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <GlassContainer
            variant="clear"
            size={2}
            style={{ maxWidth: '640px', width: '100%', maxHeight: 'min(85vh, 720px)' }}
            contentStyle={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              maxHeight: 'min(85vh, 720px)',
              overflow: 'hidden',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '500', color: '#222222', fontFamily: "'Montserrat', sans-serif" }}>
              {`Module ${modulePosition} results`}
            </h2>
            <div
              style={{
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
                paddingRight: '6px',
              }}
            >
              {moduleInstantFeedbackRows.map((row, ri) => (
                <div
                  key={ri}
                  style={{
                    borderBottom: ri < moduleInstantFeedbackRows.length - 1 ? '1px solid #eeeeee' : 'none',
                    paddingBottom: ri < moduleInstantFeedbackRows.length - 1 ? '18px' : '0',
                  }}
                >
                  <p style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: '600', color: '#222222', fontFamily: "'Montserrat', sans-serif", lineHeight: 1.35 }}>
                    {row.questionText}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#999999', fontFamily: "'Montserrat', sans-serif" }}>Your answer</p>
                      <GlassContainer
                        variant={getChoiceStyle(row.chosenLetter).variant}
                        size={0}
                        style={{ width: '100%' }}
                        contentStyle={{
                          padding: '10px 14px',
                          display: 'block',
                        }}
                      >
                        <span style={{ fontSize: '14px', fontWeight: '500', color: getChoiceStyle(row.chosenLetter).color, fontFamily: "'montserrat', sans-serif", lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {row.chosenText || '—'}
                        </span>
                      </GlassContainer>
                      {row.explanationChosen ? (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666666', lineHeight: 1.5, fontFamily: "'Montserrat', sans-serif" }}>
                          {row.explanationChosen}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#999999', fontFamily: "'Montserrat', sans-serif" }}>Correct answer</p>
                      <GlassContainer
                        variant={getChoiceStyle(row.correctLetter).variant}
                        size={0}
                        style={{ width: '100%' }}
                        contentStyle={{
                          padding: '10px 14px',
                          display: 'block',
                        }}
                      >
                        <span style={{ fontSize: '14px', fontWeight: '500', color: getChoiceStyle(row.correctLetter).color, fontFamily: "'montserrat', sans-serif", lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {row.correctText || '—'}
                        </span>
                      </GlassContainer>
                      {row.explanationCorrect ? (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666666', lineHeight: 1.5, fontFamily: "'Montserrat', sans-serif" }}>
                          {row.explanationCorrect}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginLeft: 'auto', marginTop: '8px', flexShrink: 0 }}>
              <GlassContainer
                variant="green"
                size={0}
                onClick={() => {
                  setShowModuleInstantFeedback(false);
                  setModuleInstantFeedbackRows([]);
                  advanceModuleOrFinish();
                }}
                style={{ cursor: 'pointer', minWidth: '120px' }}
                contentStyle={{
                  padding: '10px 22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ color: '#2BB514', fontSize: '0.9rem', fontWeight: '500', fontFamily: "'Montserrat', sans-serif" }}>{moduleSubmitContinueLabel}</span>
              </GlassContainer>
            </div>
          </GlassContainer>
        </div>
      )}

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        zIndex: 100,
        borderBottom: isScrolled ? '1px solid #ddd' : '1px solid transparent',
        transition: 'border-color 0.2s ease',
        padding: '10px 2%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        boxSizing: 'border-box',
      }}>
        <h1 style={{
          margin: 0,
          flex: '0 1 auto',
          fontSize: '1.15rem',
          color: 'black',
          fontWeight: '400',
          fontFamily: "'Montserrat', sans-serif",
          maxWidth: 'min(280px, 38vw)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {assignmentName}
        </h1>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '1 1 220px',
          gap: '12px',
          flexWrap: 'wrap',
          minWidth: '0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', color: '#999999', fontFamily: "'montserrat', sans-serif" }}>Modules</span>
            {moduleOrder.map((modNum) => {
              const isCurrent = modNum === currentModule;
              const doneModule = lockedSectorNumbers.includes(modNum);
              const modVariant = doneModule ? 'green' : isCurrent ? 'blue' : 'clear';
              return (
                <GlassContainer
                  key={modNum}
                  variant={modVariant}
                  size={0}
                  title={`Module ${modNum}`}
                  style={{ flexShrink: 0, cursor: 'default' }}
                  contentStyle={{
                    padding: modVariant === 'clear' ? '5px 6px' : '6px 7px',
                    minWidth: modVariant === 'clear' ? '10px' : '12px',
                    minHeight: modVariant === 'clear' ? '10px' : '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              );
            })}
          </div>
          <div style={{ width: '1px', height: '22px', background: '#e5e5e5', flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', color: '#999999', fontFamily: "'montserrat', sans-serif" }}>Questions</span>
            {moduleQuestionsList.map((q, i) => {
              const k = questionKey(q);
              const checked = isQuestionMarkedCompleted(q, completedQuestions);
              const draft =
                Boolean(moduleResponses[k]?.choice) ||
                Boolean(q.questionId != null && moduleResponses[q.questionId]?.choice);
              const active = i === moduleQuestionIndex && !showModuleReview;
              let qVariant = 'clear';
              if (checked) qVariant = 'green';
              else if (active) qVariant = 'blue';
              else if (draft) qVariant = 'green';
              return (
                <GlassContainer
                  key={k || `nav-q-${i}`}
                  variant={qVariant}
                  size={0}
                  onClick={() => {
                    closeFeedbackOnly();
                    setShowModuleReview(false);
                    setModuleQuestionIndex(i);
                  }}
                  title={`Question ${i + 1}${checked ? ' (checked)' : draft ? ' (selected)' : ''}`}
                  style={{ flexShrink: 0, cursor: 'pointer' }}
                  contentStyle={{
                    padding: qVariant === 'clear' ? '4px 5px' : '5px 6px',
                    minWidth: qVariant === 'clear' ? '8px' : '10px',
                    minHeight: qVariant === 'clear' ? '8px' : '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              );
            })}
            <GlassContainer
              variant={showModuleReview ? 'green' : 'clear'}
              size={0}
              onClick={() => {
                closeFeedbackOnly();
                setShowModuleReview(true);
              }}
              title="Review module"
              style={{ flexShrink: 0, cursor: 'pointer' }}
              contentStyle={{
                padding: '5px 7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlignJustify size={15} strokeWidth={2} color={showModuleReview ? '#2BB514' : '#777777'} />
            </GlassContainer>
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: 'auto',
          gap: '10px',
          marginRight: '0',
          flexWrap: 'wrap',
        }}>
          {allModuleQuestionsChecked && !showModuleInstantFeedback ? (
            <GlassContainer
              variant="green"
              size={0}
              onClick={handleNextModule}
              style={{ cursor: 'pointer' }}
              contentStyle={{
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ color: '#2BB514', fontSize: '0.75rem', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" }}>
                {moduleSubmitContinueLabel}
              </span>
            </GlassContainer>
          ) : null}

          <button
            type="button"
            title="Show tutorial"
            onClick={() => setShowTutorial(true)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '6px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircleHelp size={22} color="#666666" strokeWidth={1.5} />
          </button>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            {showTimer && timeLimit > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'grey',
                cursor: 'pointer',
              }} onClick={toggleTimer}>
                {showTimer ? <Eye size={16} /> : <Hourglass size={16} />}
                <span style={{ fontSize: '.9rem', fontWeight: '500' }}>
                  {showTimer ? formatTime(secondsLeft) : ''}
                </span>
              </div>
            )}
            
            <div style={{ width: '1px', height: '20px', background: '#ddd' }} />
            
            <div 
              style={{
                color: 'grey',
                display: 'flex',
                alignItems: 'center',
                position: 'relative'
              }}
              onMouseEnter={() => setShowLockTooltip(true)}
              onMouseLeave={() => setShowLockTooltip(false)}
            >
              {lockdown ? <Lock size={16} /> : <LockOpen size={16} />}
              {showLockTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '25px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'white',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: '1px solid #ddd',
                  whiteSpace: 'nowrap',
                  zIndex: 1000,
                  fontSize: '12px',
                  color: 'grey'
                }}>
                  {lockdown ? "Cannot leave tab or change window" : "Can leave tab or change window"}
                </div>
              )}
            </div>
          </div>

          {saveAndExit && (
            <button
              onClick={onSaveAndExit}
              style={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '50px',
                padding: '5px 15px',
                color: 'grey',
                cursor: 'pointer',
                fontSize: '.9rem',
                fontWeight: '400',
                width: '130px',
                fontFamily: "'Montserrat', sans-serif"
              }}
            >
              Save & Exit
            </button>
          )}

          <GlassContainer
            onClick={handleSubmitClick}
            variant="clear"
            size={0}
            style={{
              width: '130px',
              cursor: 'pointer'
            }}
            contentStyle={{
              padding: '5px 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span style={{
              color: 'grey',
              fontSize: '.9rem',
              fontWeight: '400',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              Submit
            </span>
          </GlassContainer>
        </div>
      </div>

      {/* Add spacer for fixed header */}
      <div style={{ height: '88px' }} />

      {!showTutorial ? (
        <>
          <button
            type="button"
            aria-label="Previous question"
            onClick={handlePrevQuestionInModule}
            style={{
              position: 'fixed',
              left: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 95,
              border: 'none',
              background: 'transparent',
              cursor: moduleQuestionsList.length <= 1 && !showModuleReview ? 'not-allowed' : 'pointer',
              opacity: moduleQuestionsList.length <= 1 && !showModuleReview ? 0.3 : 0.85,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 4px',
            }}
          >
            <ArrowLeft size={44} color="#333333" strokeWidth={2.25} />
          </button>
          <button
            type="button"
            aria-label={showModuleReview ? 'Next' : 'Next question'}
            onClick={() => {
              if (!showModuleReview) handleNextQuestionInModule();
            }}
            style={{
              position: 'fixed',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 95,
              border: 'none',
              background: 'transparent',
              cursor: showModuleReview ? 'not-allowed' : 'pointer',
              opacity: showModuleReview ? 0.3 : 0.85,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 4px',
            }}
          >
            <ArrowRight size={44} color="#333333" strokeWidth={2.25} />
          </button>
        </>
      ) : null}

      <div style={{
        width: '800px',
        maxWidth: '90%',
        margin: '40px auto',
        position: 'relative',
        paddingLeft: '48px',
        paddingRight: '48px',
        boxSizing: 'border-box',
      }}>
        {showModuleReview ? (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: '1.35rem',
              fontWeight: '500',
              color: '#222222',
              fontFamily: "'Montserrat', sans-serif",
            }}>
              {`Review module ${modulePosition}`}
            </h2>
            <p style={{
              margin: '0 0 20px 0',
              color: '#666666',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              fontFamily: "'Montserrat', sans-serif",
            }}>
              Tap a question to go back and change your answer. When you are ready, check the whole module at once.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {moduleQuestionsList.map((q, i) => {
                const k = questionKey(q);
                const done = completedQuestions.includes(k);
                let letter = '';
                let line = '';
                if (done) {
                  const corr = correctQuestions.find((c) => c && c.question === q.question);
                  const inc = incorrectQuestions.find((x) => x && x.question === q.question);
                  const lett = String(corr?.correctChoice || inc?.chosenAnswer || '').toLowerCase();
                  letter = lett ? lett.toUpperCase() : '';
                  line = lett ? choiceText(q, lett) : '';
                } else {
                  const r = moduleResponses[k];
                  const lett = (r?.choice || '').toLowerCase();
                  letter = lett ? lett.toUpperCase() : '';
                  line = lett ? choiceText(q, lett) : '';
                }
                const lettLower = letter ? letter.toLowerCase() : '';
                const sty = lettLower ? getChoiceStyle(lettLower) : { variant: 'clear', color: '#888888' };
                return (
                  <div
                    key={k || `rev-${i}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      closeFeedbackOnly();
                      setShowModuleReview(false);
                      setModuleQuestionIndex(i);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        closeFeedbackOnly();
                        setShowModuleReview(false);
                        setModuleQuestionIndex(i);
                      }
                    }}
                    style={{ width: '100%', cursor: 'pointer', paddingBottom: '4px' }}
                  >
                    <span style={{ fontSize: '11px', color: '#999999', fontFamily: "'montserrat', sans-serif" }}>{`Question ${i + 1}`}</span>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#222222', fontFamily: "'montserrat', sans-serif", lineHeight: 1.35, marginTop: '4px', marginBottom: '10px' }}>
                      {q.question}
                    </div>
                    {line ? (
                      <GlassContainer
                        variant={sty.variant}
                        size={0}
                        style={{ width: '100%' }}
                        contentStyle={{ padding: '10px 14px', display: 'block' }}
                      >
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: sty.color,
                          fontFamily: "'montserrat', sans-serif",
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {line}
                        </span>
                      </GlassContainer>
                    ) : (
                      <span style={{ fontSize: '14px', color: '#bbbbbb', fontFamily: "'montserrat', sans-serif" }}>—</span>
                    )}
                  </div>
                );
              })}
            </div>
            {!allModuleQuestionsChecked ? (
              <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                <GlassContainer
                  variant="green"
                  size={0}
                  onClick={handleCheckEntireModule}
                  style={{ cursor: 'pointer' }}
                  contentStyle={{
                    padding: '12px 28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ color: '#2BB514', fontSize: '0.95rem', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" }}>Next Module</span>
                </GlassContainer>
              </div>
            ) : (
              <p style={{
                margin: '24px 0 0 0',
                fontSize: '13px',
                color: '#888888',
                textAlign: 'center',
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: 1.5,
              }}>
                This module is already checked. Use Next module or Finish in the header when you are ready to continue.
              </p>
            )}
          </div>
        ) : currentQuestion ? (
          <>
            <div style={{
              position: 'absolute',
              top: -15,
              left: '0',
              right: '0',
              height: 'fit-content',
              minHeight: '50px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridAutoRows: '14px',
              alignContent: 'start',
              pointerEvents: 'none',
              zIndex: 0,
              padding: '0px 0'
            }}>
              {Array(15).fill("AI ASSISTANCE PROHIBITED - ACADEMIC INTEGRITY  - NO BYPASS -").map((text, i) => (
                <div
                  key={i}
                  style={{
                    color: '#ddd',
                    fontSize: '9px',
                    padding: '2px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                    userSelect: 'none',
                    fontWeight: '600',
                    WebkitUserSelect: 'none',
                    msUserSelect: 'none',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '0.5px'
                  }}
                >
                  {text}
                </div>
              ))}
            </div>

            <div style={{
              width: '100%',
              backgroundColor: 'transparent',
              color: 'black',
              fontWeight: '600',
              fontSize: '1.5rem',
              padding: '5px 15px',
              textAlign: 'left',
              position: 'relative',
              zIndex: 1,
              marginBottom: '12px',
              userSelect: 'none',
              WebkitTextStroke: '0.5px white',
              textShadow: '0 0 1px white'
            }}>
              {currentQuestion.question}
            </div>

            <p style={{
              color: 'grey',
              marginBottom: '16px',
              fontWeight: '400',
              fontSize: '14px',
              marginTop: '12px',
              fontFamily: "'montserrat', sans-serif",
            }}>
              {assignment.retypeEnabled === false
                ? (selectedAnswer ? 'Choice saved. Use the last Questions bubble (summary icon) or the last arrow step when you are ready to review.' : 'Click a choice to begin.')
                : selectedAnswer
                  ? isTypedAnswerCorrect()
                    ? 'Retype complete. Open review from the last Questions bubble or by going past the last question.'
                    : 'Type the full answer text to match your choice.'
                  : 'Click a choice to begin.'}
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              marginTop: '8px',
              position: 'relative',
              zIndex: 1
            }}>
              {choiceLetters(currentQuestion).map((letter) => (
                <React.Fragment key={letter}>{renderChoice(letter)}</React.Fragment>
              ))}
            </div>
          </>
        ) : null}

        <AnimatePresence>
          {showFeedback && currentQuestion && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              style={{ width: 'calc(92% - 300px )', left: '200px', position: 'fixed', top: '100px', background: 'white', height: '700px', padding: '20px' }}
            >
              {assignment.feedback === 'instant' ? (
                (selectedAnswer || '').toLowerCase() === (currentQuestion.correctChoice || '').toLowerCase() ? (
                  <div>
                    <Confetti
                      width={window.innerWidth}
                      height={window.innerHeight}
                      recycle={false}
                      numberOfPieces={100}
                      confettiSource={{
                        x: window.innerWidth * 0.37,
                        y: window.innerHeight * 0.28,
                        w: 30,
                        h: 0,
                      }}
                      colors={['#A3F2ED', '#AEF2A3', '#F8CFFF', '#FFECA8', '#FFD1D1']}
                      initialVelocityY={20}
                      initialVelocityX={5}
                      gravity={0.18}
                      tweenDuration={400}
                      spread={1080}
                      run={showConfetti}
                    />
                    <div style={{ marginTop: '20%', position: "absolute", left: '50%', transform: 'translatex(-50%)' }}>
                      <SquareCheck size={200} color="#00c721" strokeWidth={2} />
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '150px', marginLeft: '4%' }}>
                    <div style={{ display: 'flex', height: '60px' }}>
                      <SquareX size={60} color="red" strokeWidth={2} />
                      <h1 style={{ fontSize: '60px', marginTop: '-5px', marginLeft: '30px', fontWeight: '600' }}>Almost there...</h1>
                    </div>
                    <p style={{
                      width: '750px',
                      fontSize: '25px',
                      marginTop: '80px',
                      color: 'grey',
                      fontWeight: '500',
                    }}>
                      {explanationAt(currentQuestion, (selectedAnswer || 'a').toLowerCase())}
                    </p>
                    <button
                      style={{
                        width: '170px',
                        backgroundColor: '#FFF4D0',
                        height: '40px',
                        borderRadius: '5px',
                        color: '#FFC400',
                        border: '1px solid #FFAE00',
                        cursor: 'pointer',
                        fontFamily: "'montserrat', sans-serif",
                        fontWeight: '600',
                        fontSize: '20px',
                        position: 'absolute',
                        bottom: '200px',
                      }}
                      onClick={() => setShowExplanation(true)}
                    >
                      Next
                    </button>
                  </div>
                )
              ) : (
                (selectedAnswer || '').toLowerCase() === (currentQuestion.correctChoice || '').toLowerCase() ? (
                  <div>
                    <Confetti
                      width={window.innerWidth}
                      height={window.innerHeight}
                      recycle={false}
                      numberOfPieces={100}
                      confettiSource={{
                        x: window.innerWidth * 0.37,
                        y: window.innerHeight * 0.28,
                        w: 30,
                        h: 0,
                      }}
                      colors={['#A3F2ED', '#AEF2A3', '#F8CFFF', '#FFECA8', '#FFD1D1']}
                      initialVelocityY={20}
                      initialVelocityX={5}
                      gravity={0.18}
                      tweenDuration={400}
                      spread={1080}
                      run={showConfetti}
                    />
                    <div style={{
                      width: '200px',
                      height: '200px',
                      borderRadius: '200px',
                      marginTop: '20%',
                      border: '20px solid #00B512',
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}>
                      <SquareCheck style={{color:'green'}} width={100} />
                    </div>
                    <h1 style={{ fontSize: '60px', color: '#00B512' }}>Correct!</h1>
                  </div>
                ) : (
                  <div style={{ marginTop: '100px' }}>
                    <SquareX style={{color:'red'}} width={100} />
                    <h1 style={{ fontSize: '60px', color: '#FF0000' }}>Incorrect</h1>
                  </div>
                )
              )}
            </motion.div>
          )}
          {showExplanation && currentQuestion && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              style={{ width: '840px', position: 'fixed', top: '140px', background: 'white', height: '700px', padding: '0px', left: 'calc(200px + 4%)' }}
            >

              
<div style={{     display: 'flex',
              flexWrap: 'wrap',
              width: '840px',
              background: 'white',
              marginLeft: 'auto', marginRight: 'auto',
              borderRadius: '30px',
              
              borderTop: 'none',
              position: 'relative', }}>



 
<div
              style={{
                width: '830px',
                borderLeft: '8px solid #FF2727',
                color: 'black',
                
                position: 'relative',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '25px', 
                padding:'10px 20px ',
              }}
            >
             
                {currentQuestion.question}
           
            </div>
                <div style={{justifyContent:'left', alignItems: 'left', justifyItems: 'left', }}>
              <h1 style={{ fontSize: '16px',fontWeight: '600', marginTop: '40px', color: 'lightgrey', marginBottom: '5px' }}>The Correct Answer is</h1>
              <div
                style={{
                  width: '720px',
                  fontSize: '20px',
                  color: 'black',
                  fontWeight: '500',
                  padding: '15px',
                  border: '1px solid lightgrey',
                  borderRadius: '10px',
                  position: 'relative',
                  minHeight: '60px',
                }}
              >
                <p
                  style={{
                    margin: 0,
                    color: retypedAnswer ? 'lightgrey' : 'lightgrey',
                    pointerEvents: 'none',
                    textAlign: 'left',
                  }}
                >
                  {choiceText(currentQuestion, (currentQuestion.correctChoice || 'a').toLowerCase())}
                </p>
                <textarea
                  value={retypedAnswer}
                  onChange={handleRetypeAnswer}
                  style={{
                    fontFamily: "'montserrat', sans-serif",
                    fontWeight: '500',
                    position: 'absolute',
                    top: '0',
                    left: '0px',
                    width: '700px',
                    height: '100%',
                    fontSize: '20px',
                    textAlign: 'left',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'black',
                    resize: 'none',
                    overflow: 'hidden',
                    padding: '15px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                />
              </div>
             
              <p
                style={{
                  width: '780px',
                  fontSize: '20px',
                  color: 'grey',
                  textAlign: 'left',
                  
                  fontWeight: '500',
                  marginTop: '15px',
                  
                }}
              >
                {explanationAt(currentQuestion, (currentQuestion.correctChoice || 'a').toLowerCase())}
              </p>

              <div style={{ marginTop: '40px' }}>
        <button
          onClick={() => setShowMyResponse(true)}
          style={{
            display: !showMyResponse ? 'flex' : 'none',
            alignItems: 'center',
            gap: '8px',
            background: '#f4f4f4',
            padding: '5px 10px',
            borderRadius: '5px',
            marginBottom: '30px',
            marginLeft: '-5px',
            border: 'none',
            cursor: 'pointer',
            color: 'grey',
            fontFamily: "'montserrat', sans-serif",
            fontWeight: '500',
            fontSize: '20px',
          }}
        >
          My Response <ChevronDown size={24} />
        </button>

        <AnimatePresence>
          {showMyResponse && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ width: '109%', borderTop: '1px solid lightgrey' }}
            >
              <div style={{  textAlign: 'left', marginBottom: '30px', marginTop: '30px'  }}>
               
                  <h1 style={{ fontSize: '16px', fontWeight: '500', color: 'lightgrey' }}>My Response</h1>
                  <p style={{
                   
                    fontSize: '20px',
                    border: '1px solid red',
                    borderRadius: '5px',
                    padding: '10px',
                    color: '#FF0000',
                    
                    textAlign: 'left',
                    background: '#FFB6B6',
                    fontWeight: '500',
                    marginTop: '-5px',
                  }}>
                    {choiceText(currentQuestion, (selectedAnswer || 'a').toLowerCase())}
                  </p>
              
                  <p style={{
                    width: '100%',
                    fontSize: '20px',
                    color: 'grey',
                    textAlign: 'left',
                    fontWeight: '500',
                    marginTop: '20px',
                  }}>
                    {explanationAt(currentQuestion, (selectedAnswer || 'a').toLowerCase())}
                  </p>
               
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div>
        <button
          style={{
            width: '200px',
            backgroundColor: isRetypedAnswerCorrect() ? '#FFF4D0' : '#dddCCC',
            height: '40px',
            borderRadius: '5px',
            border: isRetypedAnswerCorrect() ? '1px solid #FFCF32' : '1px solid grey',
            color: isRetypedAnswerCorrect() ? '#FFC400' : 'grey',
        
            cursor: isRetypedAnswerCorrect() ? 'pointer' : 'not-allowed',
            fontFamily: "'montserrat', sans-serif",
            fontWeight: '500',
            fontSize: '20px',
            marginTop: '20px',
            marginBottom: '30px'
          }}
          onClick={() => {
            if (isRetypedAnswerCorrect()) {
              setRetypedAnswer('');
              closeFeedbackOnly();
            }
          }}
          disabled={!isRetypedAnswerCorrect()}
        >
          Next Question
        </button>
        </div>
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    
  );
};

export default TakeAmcq;